import { useState, useEffect, useRef, useCallback } from 'react';
import { Palette, Upload, Trash2, Save, Image, Building2, MapPin, Plus, Pencil, Eye, EyeOff, Upload as UploadIcon, X, Users, FileText } from 'lucide-react';
import { useCustomers } from '../../hooks/useAdminData';
import { contractLensService } from '../../services/contractlens.service';
import type { AdminCustomerSettings, AdminArea } from '../../services/contractlens.service';

/**
 * Reads an error message from an API failure. `unknown` is the honest type
 * for a caught value; this narrows it without reaching for `any`.
 */
function apiErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const data = (err as { response?: { data?: { error?: string } } }).response?.data;
    if (data && typeof data.error === 'string') return data.error;
  }
  return fallback;
}

export function ContractLensAdmin() {
  const [search, setSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [settings, setSettings] = useState<AdminCustomerSettings | null>(null);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [logoBlobUrl, setLogoBlobUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Color form state
  const [pageBackgroundColor, setPageBackgroundColor] = useState('#0f172a');
  const [navbarBackgroundColor, setNavbarBackgroundColor] = useState('#1e293b');
  const [navbarTextColor, setNavbarTextColor] = useState('#ffffff');
  const [pageTextColor, setPageTextColor] = useState('#e2e8f0');
  const [pageHeadingColor, setPageHeadingColor] = useState('#f8fafc');
  const [bannerBackgroundColor, setBannerBackgroundColor] = useState('#dc2626');

  // Area management state
  const [areas, setAreas] = useState<AdminArea[]>([]);
  const [isLoadingAreas, setIsLoadingAreas] = useState(false);
  const [editingArea, setEditingArea] = useState<AdminArea | null>(null);
  const [isCreatingArea, setIsCreatingArea] = useState(false);
  const [areaForm, setAreaForm] = useState({ name: '', description: '', isPublic: false, allowPublicUpload: false });
  const [isSavingArea, setIsSavingArea] = useState(false);
  const [isDeletingAreaId, setIsDeletingAreaId] = useState<string | null>(null);

  const { data: customersData, isLoading: isLoadingCustomers } = useCustomers(search);
  const customers = customersData?.customers ?? [];

  // Clear success after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadSettings = useCallback(async (customerId: string) => {
    setIsLoadingSettings(true);
    setError(null);
    // Revoke the previous blob URL. Read it through the setter rather than
    // as a dependency: depending on logoBlobUrl here would recreate this
    // callback each time it loads a logo, and the effect would then loop.
    setLogoBlobUrl((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return null;
    });
    try {
      const data = await contractLensService.getAdminCustomerSettings(customerId);
      setSettings(data);
      setPageBackgroundColor(data.pageBackgroundColor || '#0f172a');
      setNavbarBackgroundColor(data.navbarBackgroundColor || '#1e293b');
      setNavbarTextColor(data.navbarTextColor || '#ffffff');
      setPageTextColor(data.pageTextColor || '#e2e8f0');
      setPageHeadingColor(data.pageHeadingColor || '#f8fafc');
      setBannerBackgroundColor(data.bannerBackgroundColor || '#dc2626');
      // Fetch logo blob
      if (data.hasLogo) {
        try {
          const url = await contractLensService.getAdminCustomerLogoBlob(customerId);
          setLogoBlobUrl(url);
        } catch {
          // Logo fetch failed, that's ok
        }
      }
    } catch (err) {
      setError('Failed to load settings');
      console.error(err);
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  const loadAreas = useCallback(async (customerId: string) => {
    setIsLoadingAreas(true);
    try {
      const data = await contractLensService.getAdminCustomerAreas(customerId);
      setAreas(data);
    } catch (err) {
      console.error('Failed to load areas', err);
    } finally {
      setIsLoadingAreas(false);
    }
  }, []);

  useEffect(() => {
    if (selectedCustomerId) {
      loadSettings(selectedCustomerId);
      loadAreas(selectedCustomerId);
    }
  }, [selectedCustomerId, loadSettings, loadAreas]);

  const handleSaveColors = async () => {
    if (!selectedCustomerId) return;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await contractLensService.updateAdminCustomerColors(selectedCustomerId, {
        pageBackgroundColor,
        navbarBackgroundColor,
        navbarTextColor,
        pageTextColor,
        pageHeadingColor,
        bannerBackgroundColor,
      });
      setSettings(updated);
      setSuccess('Colors saved successfully');
    } catch (err) {
      setError('Failed to save colors');
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedCustomerId) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only image files are allowed (PNG, JPEG, GIF, WebP, SVG)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('File must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setError(null);
    try {
      await contractLensService.uploadAdminCustomerLogo(selectedCustomerId, file);
      await loadSettings(selectedCustomerId);
      setSuccess('Logo uploaded successfully');
    } catch (err) {
      setError('Failed to upload logo');
      console.error(err);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteLogo = async () => {
    if (!selectedCustomerId) return;
    setIsDeleting(true);
    setError(null);
    try {
      await contractLensService.deleteAdminCustomerLogo(selectedCustomerId);
      await loadSettings(selectedCustomerId);
      setSuccess('Logo deleted');
    } catch (err) {
      setError('Failed to delete logo');
      console.error(err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStartCreateArea = () => {
    setIsCreatingArea(true);
    setEditingArea(null);
    setAreaForm({ name: '', description: '', isPublic: false, allowPublicUpload: false });
  };

  const handleStartEditArea = (area: AdminArea) => {
    setEditingArea(area);
    setIsCreatingArea(false);
    setAreaForm({
      name: area.name,
      description: area.description || '',
      isPublic: area.isPublic,
      allowPublicUpload: area.allowPublicUpload,
    });
  };

  const handleCancelAreaForm = () => {
    setEditingArea(null);
    setIsCreatingArea(false);
    setAreaForm({ name: '', description: '', isPublic: false, allowPublicUpload: false });
  };

  const handleSaveArea = async () => {
    if (!selectedCustomerId || !areaForm.name.trim()) return;
    setIsSavingArea(true);
    setError(null);
    try {
      if (isCreatingArea) {
        await contractLensService.createAdminCustomerArea(selectedCustomerId, {
          name: areaForm.name.trim(),
          description: areaForm.description.trim() || undefined,
          isPublic: areaForm.isPublic,
          allowPublicUpload: areaForm.allowPublicUpload,
        });
        setSuccess('Area created successfully');
      } else if (editingArea) {
        await contractLensService.updateAdminCustomerArea(selectedCustomerId, editingArea.id, {
          name: areaForm.name.trim(),
          description: areaForm.description.trim() || undefined,
          isPublic: areaForm.isPublic,
          allowPublicUpload: areaForm.allowPublicUpload,
        });
        setSuccess('Area updated successfully');
      }
      handleCancelAreaForm();
      await loadAreas(selectedCustomerId);
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, 'Failed to save area');
      setError(msg);
      console.error(err);
    } finally {
      setIsSavingArea(false);
    }
  };

  const handleDeleteArea = async (areaId: string) => {
    if (!selectedCustomerId) return;
    setIsDeletingAreaId(areaId);
    setError(null);
    try {
      await contractLensService.deleteAdminCustomerArea(selectedCustomerId, areaId);
      setSuccess('Area deleted');
      await loadAreas(selectedCustomerId);
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, 'Failed to delete area');
      setError(msg);
      console.error(err);
    } finally {
      setIsDeletingAreaId(null);
    }
  };

  const selectedCustomerName = customers.find(c => c.id === selectedCustomerId)?.name;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">ContractLens Settings</h1>
        <p className="text-slate-600 mt-1">Manage branding, colors, and areas per customer</p>
      </div>

      {/* Customer Selector */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <label className="block text-sm font-medium text-slate-700 mb-2">Select Customer</label>
        <div className="flex gap-3">
          <input
            type="text"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        {isLoadingCustomers ? (
          <p className="text-sm text-slate-500 mt-2">Loading...</p>
        ) : customers.length > 0 ? (
          <div className="mt-3 max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
            {customers.map(customer => (
              <button
                key={customer.id}
                onClick={() => setSelectedCustomerId(customer.id)}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors ${
                  selectedCustomerId === customer.id ? 'bg-blue-50 text-blue-700' : 'text-slate-700'
                }`}
              >
                <Building2 size={16} className={selectedCustomerId === customer.id ? 'text-blue-500' : 'text-slate-400'} />
                <span className="text-sm font-medium">{customer.name}</span>
              </button>
            ))}
          </div>
        ) : search ? (
          <p className="text-sm text-slate-500 mt-2">No customers found</p>
        ) : null}
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
          {success}
        </div>
      )}

      {/* Settings Panel */}
      {selectedCustomerId && (
        <>
          {isLoadingSettings ? (
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-500">
              Loading settings...
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Areas Management */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin size={20} className="text-blue-600" />
                    <h2 className="text-lg font-semibold text-slate-800">Areas</h2>
                  </div>
                  <button
                    onClick={handleStartCreateArea}
                    disabled={isCreatingArea}
                    className="flex items-center gap-1.5 text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <Plus size={16} />
                    New Area
                  </button>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Manage areas (Kommersielle, Anlegg, etc.) for <strong>{selectedCustomerName}</strong>. Control visibility and upload permissions.
                </p>

                {/* Create/Edit form */}
                {(isCreatingArea || editingArea) && (
                  <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-700">
                        {isCreatingArea ? 'Create New Area' : `Edit: ${editingArea?.name}`}
                      </h3>
                      <button onClick={handleCancelAreaForm} className="text-slate-400 hover:text-slate-600">
                        <X size={18} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                        <input
                          type="text"
                          value={areaForm.name}
                          onChange={(e) => setAreaForm(f => ({ ...f, name: e.target.value }))}
                          placeholder="e.g. Kommersielle, Anlegg..."
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                        <input
                          type="text"
                          value={areaForm.description}
                          onChange={(e) => setAreaForm(f => ({ ...f, description: e.target.value }))}
                          placeholder="Optional description..."
                          className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        />
                      </div>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={areaForm.isPublic}
                            onChange={(e) => setAreaForm(f => ({ ...f, isPublic: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">Public (visible to all users)</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={areaForm.allowPublicUpload}
                            onChange={(e) => setAreaForm(f => ({ ...f, allowPublicUpload: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">Allow public upload</span>
                        </label>
                      </div>
                      <button
                        onClick={handleSaveArea}
                        disabled={isSavingArea || !areaForm.name.trim()}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm"
                      >
                        <Save size={16} />
                        {isSavingArea ? 'Saving...' : isCreatingArea ? 'Create Area' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Areas list */}
                {isLoadingAreas ? (
                  <p className="text-sm text-slate-500">Loading areas...</p>
                ) : areas.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <MapPin size={32} className="mx-auto mb-2" />
                    <p className="text-sm">No areas configured for this customer.</p>
                    <p className="text-xs mt-1">Create areas like "Kommersielle" or "Anlegg" to organize contracts.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                    {areas.map(area => (
                      <div key={area.id} className="px-4 py-3 flex items-center gap-4 hover:bg-slate-50">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-800 text-sm">{area.name}</span>
                            {area.isPublic ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                                <Eye size={12} /> Public
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-600">
                                <EyeOff size={12} /> Private
                              </span>
                            )}
                            {area.allowPublicUpload && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                                <UploadIcon size={12} /> Public Upload
                              </span>
                            )}
                          </div>
                          {area.description && (
                            <p className="text-xs text-slate-500 mt-0.5 truncate">{area.description}</p>
                          )}
                          <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                            <span className="inline-flex items-center gap-1">
                              <Users size={12} /> {area.userCount} users
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <FileText size={12} /> {area.contractCount} contracts
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStartEditArea(area)}
                            className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Edit area"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteArea(area.id)}
                            disabled={isDeletingAreaId === area.id || area.contractCount > 0}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title={area.contractCount > 0 ? 'Cannot delete area with contracts' : 'Delete area'}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Colors */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Palette size={20} className="text-blue-600" />
                  <h2 className="text-lg font-semibold text-slate-800">Colors</h2>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Customize the look of ContractLens for <strong>{selectedCustomerName}</strong>
                </p>

                <div className="space-y-4">
                  <ColorInput
                    label="Page Background"
                    value={pageBackgroundColor}
                    onChange={setPageBackgroundColor}
                  />
                  <ColorInput
                    label="Navbar Background"
                    value={navbarBackgroundColor}
                    onChange={setNavbarBackgroundColor}
                  />
                  <ColorInput
                    label="Navbar Text"
                    value={navbarTextColor}
                    onChange={setNavbarTextColor}
                  />
                  <ColorInput
                    label="Page Text"
                    value={pageTextColor}
                    onChange={setPageTextColor}
                  />
                  <ColorInput
                    label="Page Headings"
                    value={pageHeadingColor}
                    onChange={setPageHeadingColor}
                  />
                  <ColorInput
                    label="Banner Background"
                    value={bannerBackgroundColor}
                    onChange={setBannerBackgroundColor}
                  />
                </div>

                <button
                  onClick={handleSaveColors}
                  disabled={isSaving}
                  className="mt-6 w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  <Save size={18} />
                  {isSaving ? 'Saving...' : 'Save Colors'}
                </button>
              </div>

              {/* Logo */}
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Image size={20} className="text-blue-600" />
                  <h2 className="text-lg font-semibold text-slate-800">Logo</h2>
                </div>
                <p className="text-sm text-slate-500 mb-4">
                  Upload a custom logo for <strong>{selectedCustomerName}</strong>
                </p>

                {/* Preview */}
                {settings?.hasLogo && logoBlobUrl ? (
                  <div className="mb-4">
                    <div className="bg-slate-900 rounded-lg p-6 flex items-center justify-center min-h-[120px]">
                      <img
                        src={logoBlobUrl}
                        alt="Customer logo"
                        className="max-h-20 max-w-full object-contain"
                      />
                    </div>
                    <p className="text-sm text-slate-500 mt-2">
                      Current: {settings.logoFileName}
                    </p>
                  </div>
                ) : (
                  <div className="mb-4 bg-slate-100 rounded-lg p-6 flex items-center justify-center min-h-[120px] text-slate-400">
                    No logo uploaded
                  </div>
                )}

                {/* Upload */}
                <div className="space-y-3">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                    onChange={handleUploadLogo}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                  >
                    <Upload size={18} />
                    {isUploading ? 'Uploading...' : 'Upload Logo'}
                  </button>

                  {settings?.hasLogo && (
                    <button
                      onClick={handleDeleteLogo}
                      disabled={isDeleting}
                      className="w-full flex items-center justify-center gap-2 text-red-600 border border-red-300 px-4 py-2.5 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors"
                    >
                      <Trash2 size={18} />
                      {isDeleting ? 'Deleting...' : 'Delete Logo'}
                    </button>
                  )}
                </div>

                <p className="text-xs text-slate-400 mt-3">
                  Max 5MB. PNG, JPEG, GIF, WebP, or SVG.
                </p>
              </div>

              {/* Preview */}
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-800 mb-4">Preview</h2>
                <div className="rounded-lg overflow-hidden border border-slate-200">
                  {/* Navbar preview */}
                  <div
                    className="px-4 py-3 flex items-center gap-3"
                    style={{ backgroundColor: navbarBackgroundColor, color: navbarTextColor }}
                  >
                    {settings?.hasLogo && logoBlobUrl ? (
                      <img
                        src={logoBlobUrl}
                        alt="Logo"
                        className="h-8 object-contain"
                      />
                    ) : (
                      <span className="font-bold">Lynki</span>
                    )}
                    <span className="ml-auto text-sm">ContractLens</span>
                  </div>
                  {/* Banner preview */}
                  <div
                    className="px-6 py-4 flex items-center gap-4"
                    style={{ backgroundColor: bannerBackgroundColor }}
                  >
                    {settings?.hasLogo && logoBlobUrl && (
                      <img src={logoBlobUrl} alt="Logo" className="h-12 object-contain" />
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-white">Velkommen tilbake</h3>
                      <p className="text-sm text-white/70">Administrer dine kontrakter</p>
                    </div>
                  </div>
                  {/* Page preview */}
                  <div
                    className="p-6 min-h-[120px]"
                    style={{ backgroundColor: pageBackgroundColor }}
                  >
                    <h3
                      className="text-lg font-bold mb-2"
                      style={{ color: pageHeadingColor }}
                    >
                      Dashboard
                    </h3>
                    <p
                      className="text-sm"
                      style={{ color: pageTextColor }}
                    >
                      This is how the ContractLens app will look with these colors.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ColorInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-10 h-10 rounded cursor-pointer border border-slate-300"
      />
      <div className="flex-1">
        <label className="block text-sm font-medium text-slate-700">{label}</label>
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="w-full text-sm px-2 py-1 border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
    </div>
  );
}

export default ContractLensAdmin;
